/**
 * Avvisi: quello che Otto ha capito da solo.
 *
 * La chat è quello che gli chiedi; qui c'è quello che ti dice lui. Ogni avviso
 * porta con sé le uscite — far rispondere Otto, trovare un tecnico vicino
 * all'immobile, comprare il pezzo — perché un avviso senza uscite è una
 * notifica, e le notifiche si imparano a ignorare.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowUpRight,
  Bell,
  Check,
  ExternalLink,
  MessageSquare,
  RefreshCw,
  ShieldCheck,
  X,
} from "lucide-react";
import {
  ALERT_AREE,
  ALERT_ETICHETTE,
  AREA_ETICHETTE,
  areaDi,
  formatEur,
  type AlertAction,
  type AlertArea,
  type AlertSeverity,
  type AlertView,
} from "@otto/shared";
import { api } from "../lib/api.ts";
import { messaggioDi } from "../lib/errori.ts";
import { useStore } from "../lib/store.tsx";
import {
  Card,
  EmptyState,
  Pill,
  Segmented,
  Skeleton,
  cx,
} from "../ui/primitives.tsx";
import { Campanella } from "../ui/Campanella.tsx";
import { ProposalCard } from "../blocks/ProposalCard.tsx";

type Tab = "aperti" | "archivio";

/** "Tutti" più le tre aree: quattro scelte, che sono già il massimo utile. */
type Filtro = "tutti" | AlertArea;

const TONI: Record<
  AlertSeverity,
  { punto: string; bordo: string; testo: string; etichetta: string }
> = {
  urgente: {
    punto: "bg-danger",
    bordo: "ring-danger/25",
    testo: "text-danger",
    etichetta: "bg-danger-soft text-danger",
  },
  attenzione: {
    punto: "bg-warn",
    bordo: "ring-warn/25",
    testo: "text-warn",
    etichetta: "bg-warn-soft text-warn",
  },
  informativo: {
    punto: "bg-line-strong",
    bordo: "ring-line",
    testo: "text-ink-soft",
    etichetta: "bg-surface-muted text-ink-soft",
  },
};

export function Alerts({
  onAskOtto,
  onOpen,
}: {
  onAskOtto: (prompt: string) => void;
  onOpen: (view: "inbox" | "listings", id?: string) => void;
}) {
  const store = useStore();
  const [tab, setTab] = useState<Tab>("aperti");
  const [alerts, setAlerts] = useState<AlertView[] | null>(null);
  const [filtro, setFiltro] = useState<Filtro>("tutti");
  const [busy, setBusy] = useState<string | null>(null);
  const [ricalcolo, setRicalcolo] = useState(false);

  const load = useCallback(
    (archivio: boolean) =>
      api
        .alerts(archivio)
        .then(({ alerts: caricati }) => setAlerts(caricati))
        .catch(() => setAlerts([])),
    [],
  );

  useEffect(() => {
    setAlerts(null);
    void load(tab === "archivio");
  }, [tab, load]);

  // Aprire la sezione vuol dire averli visti: il pallino sul menu si spegne.
  useEffect(() => {
    void api.alertsSeen().catch(() => undefined);
  }, []);

  /**
   * Gli avvisi non arrivano da soli.
   *
   * Lo stream porta solo gli aggiornamenti delle azioni: questa lista si
   * caricava al montaggio e restava ferma, anche mentre le regole giravano su
   * un webhook appena arrivato. Un ospite che segnala una perdita d'acqua
   * mentre la schermata è aperta non compariva finché non si cambiava scheda.
   *
   * Un evento nuovo nello stream vorrebbe dire toccare i tipi condivisi e il
   * punto che li emette — file di altri. Ricaricare quando la finestra torna
   * davanti costa una richiesta, non tocca niente di nessuno, e copre il caso
   * vero: si guarda il telefono, si torna su Otto, e c'è quello che è
   * successo nel frattempo.
   */
  useEffect(() => {
    const ricontrolla = () => {
      if (document.visibilityState === "visible") void load(tab === "archivio");
    };
    window.addEventListener("focus", ricontrolla);
    document.addEventListener("visibilitychange", ricontrolla);
    return () => {
      window.removeEventListener("focus", ricontrolla);
      document.removeEventListener("visibilitychange", ricontrolla);
    };
  }, [tab, load]);

  const archivia = async (alert: AlertView, come: "risolto" | "ignorato") => {
    setBusy(alert.id);
    try {
      await (come === "risolto"
        ? api.resolveAlert(alert.id)
        : api.ignoreAlert(alert.id));
      setAlerts((current) => current?.filter((a) => a.id !== alert.id) ?? null);
    } catch (errore) {
      store.notify(messaggioDi(errore));
    } finally {
      setBusy(null);
    }
  };

  /**
   * Il tap che fissa il prezzo minimo proposto.
   *
   * Non passa dalla chat: il minimo è la rete che protegge l'host dal modello,
   * e farlo scrivere al modello vorrebbe dire lasciargliela allentare. Il
   * numero l'hanno deciso le regole, qui si conferma e basta.
   */
  /**
   * Fissa il minimo su una casa o su tante, con lo stesso tap.
   *
   * Ogni casa porta **il suo** numero, calcolato sul suo storico: si raggruppa
   * la domanda, non la risposta. Se qualcuna fallisce non si archivia l'avviso
   * — altrimenti sparirebbe dallo schermo lasciando delle case scoperte, che è
   * il modo peggiore di sbagliare qui.
   */
  const fissaMinimo = async (
    alert: AlertView,
    immobili: Array<{ listingId: string; centesimi: number }>,
  ) => {
    setBusy(alert.id);
    try {
      let fatti = 0;
      for (const { listingId, centesimi } of immobili) {
        await api.setFloorPrice(listingId, centesimi);
        fatti += 1;
      }

      if (fatti < immobili.length) {
        store.notify(
          `Fissato il minimo su ${fatti} case su ${immobili.length}. Le altre restano scoperte.`,
        );
        return;
      }

      await api.resolveAlert(alert.id);
      setAlerts((current) => current?.filter((a) => a.id !== alert.id) ?? null);
      store.notify(
        immobili.length === 1
          ? `Prezzo minimo a ${formatEur(immobili[0]!.centesimi)}. Non ci scendo sotto, nemmeno se me lo chiedi.`
          : `Prezzo minimo fissato su ${immobili.length} case. Sotto non ci scendo, nemmeno se me lo chiedi.`,
      );
    } catch (errore) {
      store.notify(messaggioDi(errore));
    } finally {
      setBusy(null);
    }
  };

  /** Pagata: si segna su Otto, e l'avviso se ne va. */
  const segnaPagata = async (alert: AlertView, scadenzaId: string) => {
    setBusy(alert.id);
    try {
      const { scadenza } = await api.segnaPagata(scadenzaId);
      setAlerts((current) => current?.filter((a) => a.id !== alert.id) ?? null);
      store.notify(
        `Segnata come pagata: ${scadenza.descrizione}, ${formatEur(scadenza.importoCents)}.`,
      );
    } catch (errore) {
      store.notify(messaggioDi(errore));
    } finally {
      setBusy(null);
    }
  };

  const ricalcola = async () => {
    setRicalcolo(true);
    try {
      const { result } = await api.analyzeAlerts();
      await load(tab === "archivio");
      store.notify(
        result.nuovi === 0
          ? "Ho ricontrollato tutto: niente di nuovo."
          : `Ho trovato ${result.nuovi} cos${result.nuovi === 1 ? "a" : "e"} nuove.`,
      );
    } catch (errore) {
      store.notify(messaggioDi(errore));
    } finally {
      setRicalcolo(false);
    }
  };

  const urgenti = useMemo(
    () => alerts?.filter((a) => a.severity === "urgente").length ?? 0,
    [alerts],
  );

  /** Quanti ne cadono in ciascuna area: il numero sta sul filtro, non nel vuoto. */
  const conteggi = useMemo(() => {
    const per: Record<Filtro, number> = {
      tutti: 0,
      ospiti: 0,
      casa: 0,
      ricavi: 0,
    };
    for (const alert of alerts ?? []) {
      per.tutti += 1;
      per[areaDi(alert.kind)] += 1;
    }
    return per;
  }, [alerts]);

  const visibili = useMemo(
    () =>
      (alerts ?? []).filter(
        (a) => filtro === "tutti" || areaDi(a.kind) === filtro,
      ),
    [alerts, filtro],
  );

  return (
    <div className="scroll-soft h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-[1000px] px-5 pb-12 sm:px-8">
        {/* ── Testata e filtri: restano in alto mentre l'elenco scorre, così
            niente finisce tagliato sotto di loro cambiando scheda.

            Lo stacco dall'elenco sta FUORI di qui (`mt-4` sulle tre varianti
            sotto), non nel `pb`: lo sfondo di questa fascia è opaco e copre
            ciò che le scorre sotto, quindi ogni pixel di spazio messo qui
            dentro è sfondo, non distanza. Il contorno delle schede è un
            `ring` di un pixel disegnato *fuori* dal riquadro: appiccicato
            alla fascia spariva al primo pixel di scorrimento, e sembrava una
            scheda tagliata invece che una scheda che scorre. ── */}
        <div className="sticky top-0 z-10 -mx-5 bg-surface px-5 pb-1 pt-7 sm:-mx-8 sm:px-8">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-[26px] font-extrabold tracking-tight text-ink">
                Avvisi
              </h1>
              <p className="mt-0.5 text-[13.5px] text-ink-soft">
                {tab === "archivio"
                  ? "Risolti e messi da parte: restano qui, per ricordare."
                  : urgenti > 0
                    ? `${urgenti} cos${urgenti === 1 ? "a" : "e"} non ${urgenti === 1 ? "può" : "possono"} aspettare.`
                    : "Quello che ho notato da solo guardando il gestionale."}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="w-full sm:w-[230px]">
                <Segmented<Tab>
                  options={[
                    { id: "aperti", label: "Da vedere" },
                    { id: "archivio", label: "Archivio" },
                  ]}
                  value={tab}
                  onChange={setTab}
                />
              </div>
              <Pill
                size="sm"
                variant="ghost"
                icon={
                  <RefreshCw
                    size={14}
                    className={ricalcolo ? "animate-spin" : undefined}
                  />
                }
                onClick={ricalcola}
                loading={ricalcolo}
              >
                Ricontrolla
              </Pill>
              <Campanella />
            </div>
          </div>

          {/* ── Le tre aree ─────────────────────────────────────────────── */}
          {alerts && alerts.length > 0 ? (
            // Su una riga anche su telefono: pillole più piccole, e se non
            // bastano scorrono di lato loro, non la pagina.
            <div className="scroll-soft -mx-1 mb-1 flex gap-1.5 overflow-x-auto px-1 pb-0.5 sm:mx-0 sm:flex-wrap sm:gap-2 sm:px-0">
              <FiltroArea
                id="tutti"
                label="Tutti"
                spiega="Tutto quello che ho notato"
                n={conteggi.tutti}
                attivo={filtro === "tutti"}
                onClick={() => setFiltro("tutti")}
              />
              {ALERT_AREE.map((area) => (
                <FiltroArea
                  key={area}
                  id={area}
                  label={AREA_ETICHETTE[area].label}
                  spiega={AREA_ETICHETTE[area].spiega}
                  n={conteggi[area]}
                  attivo={filtro === area}
                  onClick={() => setFiltro(area)}
                />
              ))}
            </div>
          ) : null}
        </div>

        {/* ── Elenco ──────────────────────────────────────────────────── */}
        {!alerts ? (
          <div className="mt-4 flex flex-col gap-3">
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} className="h-[130px]" />
            ))}
          </div>
        ) : visibili.length === 0 ? (
          <EmptyState
            icon={
              tab === "aperti" ? <ShieldCheck size={26} /> : <Bell size={26} />
            }
            title={
              filtro !== "tutti"
                ? `Niente su ${AREA_ETICHETTE[filtro].label.toLowerCase()}`
                : tab === "aperti"
                  ? "Niente da segnalare"
                  : "Archivio vuoto"
            }
            hint={
              filtro !== "tutti"
                ? "Le altre aree potrebbero avere qualcosa."
                : tab === "aperti"
                  ? "Guardo i messaggi, il calendario e i task a ogni sincronizzazione. Se salta fuori qualcosa, lo trovi qui."
                  : "Qui finiscono gli avvisi risolti e quelli che hai messo da parte."
            }
          />
        ) : (
          <div className="mt-4 flex flex-col gap-3">
            {visibili.map((alert) => (
              <Avviso
                key={alert.id}
                alert={alert}
                busy={busy === alert.id}
                archiviabile={tab === "aperti"}
                onAskOtto={onAskOtto}
                onOpen={onOpen}
                onArchivia={archivia}
                onFissaMinimo={fissaMinimo}
                onSegnaPagata={segnaPagata}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Un'area, con quanti ne contiene. Zero avvisi, filtro spento. */
function FiltroArea({
  label,
  spiega,
  n,
  attivo,
  onClick,
}: {
  id: Filtro;
  label: string;
  spiega: string;
  n: number;
  attivo: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={n === 0}
      title={spiega}
      className={cx(
        "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-pill px-3 py-1.5 text-[12.5px] font-bold sm:gap-2 sm:px-3.5 sm:py-2 sm:text-[13px]",
        "transition-all duration-calm ease-soft",
        attivo
          ? "bg-ink text-ink-inverse shadow-soft"
          : n === 0
            ? "bg-surface-muted text-ink-faint/60"
            : "bg-surface-muted text-ink-soft hover:bg-surface-sunken hover:text-ink",
      )}
    >
      {label}
      <span
        className={cx(
          "tabular-nums",
          attivo ? "text-ink-inverse/60" : "text-ink-faint",
        )}
      >
        {n}
      </span>
    </button>
  );
}

// ── Un avviso ───────────────────────────────────────────────────────────────

function Avviso({
  alert,
  busy,
  archiviabile,
  onAskOtto,
  onOpen,
  onArchivia,
  onFissaMinimo,
  onSegnaPagata,
}: {
  alert: AlertView;
  busy: boolean;
  archiviabile: boolean;
  onAskOtto: (prompt: string) => void;
  onOpen: (view: "inbox" | "listings", id?: string) => void;
  onArchivia: (alert: AlertView, come: "risolto" | "ignorato") => void;
  onFissaMinimo: (
    alert: AlertView,
    immobili: Array<{ listingId: string; centesimi: number }>,
  ) => Promise<void>;
  onSegnaPagata: (alert: AlertView, scadenzaId: string) => Promise<void>;
}) {
  const tono = TONI[alert.severity];

  const esegui = (azione: AlertAction) => {
    if (azione.kind === "chat") onAskOtto(azione.prompt);
    else if (azione.kind === "apri") onOpen(azione.view, azione.id);
    else if (azione.kind === "fissa_minimo")
      void onFissaMinimo(alert, [
        { listingId: azione.listingId, centesimi: azione.centesimi },
      ]);
    else if (azione.kind === "fissa_minimi")
      void onFissaMinimo(alert, azione.immobili);
    else if (azione.kind === "segna_pagata")
      void onSegnaPagata(alert, azione.scadenzaId);
    else if (azione.kind === "link")
      window.open(azione.url, "_blank", "noopener,noreferrer");
  };

  const bozza = alert.azioni.find(
    (a): a is Extract<AlertAction, { kind: "bozza" }> => a.kind === "bozza",
  );

  return (
    <Card className={cx("flex flex-col gap-3 p-4", tono.bordo)}>
      <div className="flex items-start gap-3">
        <span
          className={cx("mt-1.5 size-2.5 shrink-0 rounded-full", tono.punto)}
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[15.5px] font-extrabold leading-snug text-ink">
              {alert.titolo}
            </h2>
            <span
              className={cx(
                "rounded-pill px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.04em]",
                tono.etichetta,
              )}
            >
              {ALERT_ETICHETTE[alert.kind]}
            </span>
          </div>

          <p className="mt-1 whitespace-pre-line text-[13.5px] leading-relaxed text-ink-soft">
            {alert.testo}
          </p>

          {alert.listingNickname ? (
            <p className="mt-1.5 text-[12px] text-ink-faint">
              {alert.listingNickname}
            </p>
          ) : null}
        </div>

        {archiviabile ? (
          <div className="flex shrink-0 gap-1">
            <button
              type="button"
              aria-label="Segna come risolto"
              title="Segna come risolto"
              disabled={busy}
              onClick={() => onArchivia(alert, "risolto")}
              className="grid size-8 place-items-center rounded-field text-ink-faint transition-all duration-quick hover:bg-ok-soft hover:text-ok active:scale-95"
            >
              <Check size={16} />
            </button>
            <button
              type="button"
              aria-label="Metti da parte"
              title="Metti da parte"
              disabled={busy}
              onClick={() => onArchivia(alert, "ignorato")}
              className="grid size-8 place-items-center rounded-field text-ink-faint transition-all duration-quick hover:bg-surface-muted hover:text-ink active:scale-95"
            >
              <X size={16} />
            </button>
          </div>
        ) : null}
      </div>

      {bozza ? <BozzaNellAvviso actionId={bozza.actionId} /> : null}

      <div className="flex flex-wrap gap-2">
        {alert.azioni
          .filter((azione) => azione.kind !== "bozza")
          .map((azione, index) => (
            <Pill
              key={`${azione.kind}-${index}`}
              size="sm"
              variant={azione.primaria ? "primary" : "secondary"}
              icon={iconaPer(azione)}
              disabled={busy}
              onClick={() => esegui(azione)}
            >
              {azione.label}
              {/* Il dettaglio («imposta di soggiorno: importi, scadenze…»)
                  solo su schermo largo: su telefono non va a capo e spingeva
                  l'intera pagina oltre il bordo, che poi scorreva di lato. */}
              {azione.kind === "link" && azione.dettaglio ? (
                <span className="ml-1 hidden font-medium opacity-60 sm:inline">
                  {azione.dettaglio}
                </span>
              ) : null}
            </Pill>
          ))}
      </div>
    </Card>
  );
}

/**
 * La risposta pronta, dentro l'avviso: è la proposta vera, con Approva e
 * Rifiuta, la ricevuta e i guardrail. Lo store conosce le azioni recenti; se
 * questa manca, la card se la va a prendere da sé.
 */
function BozzaNellAvviso({ actionId }: { actionId: string }) {
  const store = useStore();
  const action = store.getAction(actionId);
  const chiesta = useRef(false);
  useEffect(() => {
    if (action || chiesta.current) return;
    chiesta.current = true;
    void store.refreshAction(actionId);
  }, [action, actionId, store]);
  if (!action) {
    return (
      <p className="text-[13px] text-ink-faint">Carico la risposta pronta…</p>
    );
  }
  return <ProposalCard actionId={actionId} fallback={action.preview} />;
}

function iconaPer(azione: AlertAction) {
  if (azione.kind === "chat") return <MessageSquare size={14} />;
  if (azione.kind === "link") return <ExternalLink size={14} />;
  if (azione.kind === "fissa_minimo" || azione.kind === "fissa_minimi")
    return <ShieldCheck size={14} />;
  if (azione.kind === "segna_pagata") return <Check size={14} />;
  return <ArrowUpRight size={14} />;
}
